import * as React from 'react';
import { memo, useEffect, useState } from 'react';
import Helmet, { HelmetTags } from 'react-helmet';
import { LoginType } from '@websky/graphql';
import { useAuthenticate } from '@features/auth';
import { useConfig } from '../../context';
import { useCurrentUser } from '../../CurrentUser/CurrentUser.hooks';

const GoogleOneTapSignIn: React.FC = memo(() => {
	const {
		Engine: { googleOneTapSignInClientId }
	} = useConfig();

	if (!googleOneTapSignInClientId) {
		return null;
	}

	const [authenticate] = useAuthenticate();
	const { data: accountData, reinitialize, loading } = useCurrentUser(false);

	const [googleAccountId, setGoogleAccountId] = useState<typeof google.accounts.id>(null);

	const handleSignInResponse = async (response: google.accounts.id.CredentialResponse) => {
		const result = await authenticate({
			variables: {
				params: {
					loginType: LoginType.GoogleOneTap,
					secureCode: response.credential
				}
			}
		});

		if (result?.data?.Authenticate?.token) {
			reinitialize();
		}
	};

	const handleClientStateChange = (_: any, addedTags: HelmetTags) => {
		const script = addedTags?.scriptTags?.[0];

		if (script) {
			script.onload = () => {
				if (window.google?.accounts?.id) {
					setGoogleAccountId(window.google.accounts.id);
				}
			};
		}
	};

	useEffect(() => {
		if (googleAccountId && !accountData?.CurrentUser?.userProfile && !loading) {
			googleAccountId.initialize({
				client_id: googleOneTapSignInClientId,
				callback: handleSignInResponse
			});

			googleAccountId.prompt();
		}
	}, [googleAccountId, loading, accountData]);

	return (
		<Helmet
			async
			script={[{ src: 'https://accounts.google.com/gsi/client' }]}
			onChangeClientState={handleClientStateChange}
		/>
	);
});

export default GoogleOneTapSignIn;
